import { Component as ReactComponent, ReactElement } from 'react';
import { green, red } from '@ant-design/colors';
import { Button, Modal, Tag } from 'antd';
import { ItemTable } from 'containers/Console/ItemTable/ItemTable';
import { ColumnsType } from 'antd/lib/table';
import { CoreUtils } from 'utils/CoreUtils';
import { AuthenticationHeader, Component, Configuration, ConfigurationSpec, ConverterMap, Flow, FlowType, FlowUtils, ProcessorHeader } from '@methodset/endpoint-client-ts';
import { FlowEditor, State } from './FlowEditor/FlowEditor';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, DownOutlined, EditOutlined, SyncOutlined, UpOutlined } from '@ant-design/icons';
import { Globals } from 'constants/Globals';
import { Spacer } from 'components/Spacer/Spacer';
import { ExecutionHistory, ExecutionState, Inputs, StatusType } from '@methodset/workflow-client-ts';
import { RestUtils } from 'utils/RestUtils';
import { StateView } from './StateView/StateView';
import { AfterSaveCallback } from '../Workflows/WorkflowItem/WorkflowEditor/WorkflowEditor';
import { Clickable } from 'components/Clickable/Clickable';
import workflowService from 'services/WorkflowService';
import update from 'immutability-helper';
import './Flows.less';

const CHECK_INTERVAL = 2 * 1000;

const COLOR_MAP = CoreUtils.toColormap(CoreUtils.enumToKeys(FlowType));

export type ComponentMap = { [key: string]: Component };
export type StateMap = { [key: string]: State };
export type ExecutionMap = { [key: string]: ExecutionState };

export type EditCallback = (isEditing: boolean) => void;
export type SaveCallback = (callback: AfterSaveCallback) => void;
export type ChangeCallback = (transitions: Flow[]) => void;

export type FlowsProps = {
    className?: string,
    workflowId?: string,
    // True if the workflow has unsaved changes.
    isDirty: boolean,
    headers: ProcessorHeader[],
    components: Component[],
    flows: Flow[],
    inputs?: Inputs,
    converterMap: ConverterMap,
    configurationSpecs: ConfigurationSpec[],
    authentications: AuthenticationHeader[],
    onEdit: EditCallback,
    onSave: SaveCallback,
    onChange: ChangeCallback
}

export type FlowsState = {
    // Transition being edited.
    flow?: Flow,
    // The index of the edited component.
    index?: number,
    // True when the workflow test is running.
    isRunning: boolean,
    // True when the flow is in test mode.
    testingView: boolean,
    // The id of the current workflow execution.
    executionId?: string,
    // The execution state inputs/outputs to display.
    executionState?: ExecutionState,
    // Map flow name to execution history.
    executionMap: ExecutionMap,
    // Message to show during testing.
    testingMessage?: string,
    // Error message on loading test records.
    error?: Error
}

export class Flows extends ReactComponent<FlowsProps, FlowsState> {

    // Map of id to components.
    private componentMap: ComponentMap = {};
    // Map of id to flows.
    private stateMap: StateMap = {};
    // Timer to check states during testing.
    private timerId: NodeJS.Timeout | null = null;

    constructor(props: FlowsProps) {
        super(props);
        this.state = {
            isRunning: false,
            testingView: false,
            executionState: undefined,
            executionMap: {},
            testingMessage: undefined,
            error: undefined
        }
        this.componentMap = this.buildComponentMap(props.components);
        this.stateMap = this.buildStateMap(props.flows, props.components);
        this.handleFlowCreate = this.handleFlowCreate.bind(this);
        this.handleFlowDelete = this.handleFlowDelete.bind(this);
        this.handleFlowEdit = this.handleFlowEdit.bind(this);
        this.handleFlowMoveUp = this.handleFlowMoveUp.bind(this);
        this.handleFlowMoveDown = this.handleFlowMoveDown.bind(this);
        this.handleWorkflowTest = this.handleWorkflowTest.bind(this);
        this.handleEditShow = this.handleEditShow.bind(this);
        this.handleStateShow = this.handleStateShow.bind(this);
        this.handleStateHide = this.handleStateHide.bind(this);
        this.handleEditorDone = this.handleEditorDone.bind(this);
        this.handleEditorCancel = this.handleEditorCancel.bind(this);
    }

    private handleFlowCreate(): void {
        this.setState({ index: -1 });
        this.props.onEdit(true);
    }

    private handleFlowDelete(flow: any, index: number): void {
        const flows = update(this.props.flows, {
            $splice: [[index, 1]]
        });

        this.props.onChange(flows);
    }

    private handleFlowEdit(flow: Flow, index: number): void {
        this.setState({
            flow: flow,
            index: index
        });
        this.props.onEdit(true);
    }

    private handleFlowMoveUp(flow: Flow, index: number): void {
        const flows = update(this.props.flows, {
            $splice: [[index, 1], [index - 1, 0, flow]]
        });
        this.props.onChange(flows);
    }

    private handleFlowMoveDown(flow: Flow, index: number): void {
        const flows = update(this.props.flows, {
            $splice: [[index, 1], [index + 1, 0, flow]]
        });
        this.props.onChange(flows);
    }

    private handleWorkflowTest(): void {
        // The workflow must be saved to test it since it requires a
        // state machine that needs to be stored in order to be executed.
        if (!this.props.workflowId || this.props.isDirty) {
            const that = this;
            Modal.confirm({
                title: "Save Workflow",
                content: "You must save the workflow before testing. Would you like to save it now?",
                onOk() {
                    that.props.onSave((workflowId) => that.testWorkflowRequest(workflowId));
                }
            });
            return;
        }
        this.testWorkflowRequest(this.props.workflowId);
    }

    private handleEditShow(): void {
        this.setState({
            testingView: false,
            error: undefined
        });
    }

    private handleStateShow(flow: Flow): void {
        const executionState = this.state.executionMap[flow.name];
        if (!executionState) {
            return undefined;
        }
        this.setState({ executionState: executionState });
    }

    private handleStateHide(): void {
        this.setState({ executionState: undefined });
    }

    private handleEditorDone(flow: Flow, index: number): void {
        let flows;
        if (index !== -1) {
            // Updated flow.
            flows = update(this.props.flows, {
                [index]: { $set: flow }
            });
        } else {
            // Created flow.
            flows = update(this.props.flows, {
                $push: [flow]
            });
        }
        this.props.onChange(flows);
        this.setState({
            flow: undefined,
            index: undefined
        });
        this.props.onEdit(false);
    }

    private handleEditorCancel(): void {
        this.setState({
            flow: undefined,
            index: undefined
        });
        this.props.onEdit(false);
    }

    private testWorkflowRequest(workflowId: string): Promise<any> {
        this.setState({
            isRunning: true,
            testingView: true,
            error: undefined,
            executionMap: {}
        });
        const request = {
            workflowId: workflowId
        };
        return workflowService.testWorkflow(request,
            (response: any) => this.testWorkflowResponse(response),
            (response: any) => this.testWorkflowException(response),
            true
        );
    }

    private testWorkflowResponse(response: any): void {
        const executionId = response.data.executionId;
        // Execution started, start monitoring results.
        this.readExecutionHistoryRequest(executionId);
    }

    private testWorkflowException(response: any): void {
        const error = RestUtils.getError(response);
        this.setState({
            isRunning: false,
            error: error
        });
    }

    private readExecutionHistoryRequest(executionId: string): Promise<any> {
        const request = {
            workflowId: this.props.workflowId,
            executionId: executionId
        };
        return workflowService.readExecutionHistory(request,
            (response: any) => this.readExecutionHistoryResponse(response, executionId),
            (response: any) => this.readExecutionHistoryException(response),
            true
        );
    }

    private readExecutionHistoryResponse(response: any, executionId: string): void {
        const executionHistory: ExecutionHistory = response.data.executionHistory;
        const states = executionHistory.states;
        const executionMap = states.reduce((map: ExecutionMap, state: ExecutionState) => {
            map[state.flowName] = state;
            return map;
        }, {});
        const testingMessage = this.testingMessage(executionMap);
        this.setState({
            executionId: executionId,
            executionMap: executionMap,
            testingMessage: testingMessage
        });
        if (executionHistory.status === StatusType.NONE ||
            executionHistory.status === StatusType.STARTED ||
            executionHistory.status === StatusType.RUNNING) {
            this.timerId = setTimeout(() => this.readExecutionHistoryRequest(executionId), CHECK_INTERVAL);
        } else {
            let message;
            if (executionHistory.status === StatusType.SUCCEEDED) {
                message = undefined;
            } else if (executionHistory.status === StatusType.FAILED) {
                message = executionHistory?.error?.message;
            } else {
                message = `${CoreUtils.toCapital(executionHistory.status)}.`;
            }
            this.setState({
                isRunning: false,
                error: message ? new Error(message) : undefined
            });
            this.timerId = null;
        }
    }

    private readExecutionHistoryException(response: any): void {
        const error = RestUtils.getError(response);
        this.setState({
            isRunning: false,
            error: error
        });
        this.stopTimer();
    };

    private testingMessage(executionMap: ExecutionMap): string | undefined {
        // Check if the setup stage is running.
        let stage = executionMap["Setup"];
        if (stage && stage.status === StatusType.RUNNING) {
            return "Setting up test...";
        } else {
            // Check if any other stage is running.
            const stages = Object.values(executionMap);
            for (let i = 0; i < stages.length; i++) {
                stage = stages[i];
                if (stage && stage.status === StatusType.RUNNING) {
                    return "Running test...";
                }
            }
            // Nothing is running, no message.
            return undefined;
        }
    }

    private statusIcon(flowName: string): ReactElement | undefined {
        const state = this.state.executionMap[flowName];
        if (!state) {
            return undefined;
        }
        const status = state.status;
        switch (status) {
            case StatusType.RUNNING:
                return <SyncOutlined spin={true} />;
            case StatusType.SUCCEEDED:
                return <CheckCircleOutlined style={{ color: green[6] }} />;
            case StatusType.FAILED:
                return <CloseCircleOutlined style={{ color: red[6] }} />;
            default:
                return undefined;
        }
    }

    private buildEditColumns(): ColumnsType<any> {
        const columns: ColumnsType<any> = [{
            key: 'name',
            title: 'Name',
            width: 200,
            ellipsis: true,
            render: (flow: Flow, index: number) => {
                return (
                    <ButtonLink
                        onClick={() => this.handleFlowEdit(flow, index)}
                    >
                        {flow.name}
                    </ButtonLink>
                )
            }
        }, {
            key: 'from',
            title: 'From',
            width: 200,
            ellipsis: true,
            render: (flow: Flow, index: number) => {
                return (
                    <span>
                        {!flow.prevId ? "Workflow Start" : this.stateMap[flow.prevId].name}
                    </span>
                )
            }
        }, {
            key: 'to',
            title: 'To',
            ellipsis: true,
            render: (flow: Flow) => {
                const nextIds = FlowUtils.nextIds(flow);
                const tos = nextIds.map(nextId => this.stateMap[nextId].name);
                return (
                    <span>
                        {tos.length > 0 ? tos.join(", ") : Globals.EMPTY_FIELD}
                    </span>
                );
            }
        }, {
            key: 'type',
            title: 'Type',
            align: 'center',
            width: 150,
            render: (flow: Flow) => {
                return (
                    <Tag color={COLOR_MAP[flow.type]}>
                        {flow.type}
                    </Tag>
                );
            }
        }];
        return columns;
    }

    private buildTestColumns(): ColumnsType<any> {
        const columns: ColumnsType<any> = [{
            key: 'name',
            title: 'Name',
            width: 200,
            ellipsis: true,
            render: (_, flow: Flow) => {
                return (
                    <ButtonLink
                        onClick={() => this.handleStateShow(flow)}
                    >
                        {flow.name}
                    </ButtonLink>
                )
            }
        }, {
            key: 'from',
            title: 'From',
            width: 200,
            ellipsis: true,
            render: (_, flow: Flow) => {
                return (
                    <span>
                        {!flow.prevId ? "Workflow Start" : this.stateMap[flow.prevId].name}
                    </span>
                )
            }
        }, {
            key: 'to',
            title: 'To',
            ellipsis: true,
            render: (flow: Flow) => {
                const nextIds = FlowUtils.nextIds(flow);
                const tos = nextIds.map(nextId => this.stateMap[nextId].name);
                return (
                    <span>
                        {tos.length > 0 ? tos.join(", ") : Globals.EMPTY_FIELD}
                    </span>
                );
            }
        }, {
            key: 'type',
            title: 'Type',
            align: 'center',
            width: 150,
            render: (flow: Flow) => {
                return (
                    <Tag color={COLOR_MAP[flow.type]}>
                        {flow.type}
                    </Tag>
                );
            }
        }, {
            key: 'status',
            title: 'Status',
            align: 'center',
            width: 100,
            render: (_, flow: Flow, index: number) => {
                return (
                    <Clickable onClick={() => this.handleStateShow(flow)}>
                        {this.statusIcon(flow.name)}
                    </Clickable>
                )
            }
        }];
        return columns;
    }

    private buildComponentMap(components: Component[]): ComponentMap {
        return components.reduce((map: ComponentMap, component: Component) => {
            map[component.id] = component;
            return map;
        }, {} as ComponentMap);
    }

    private buildStateMap(flows: Flow[], components: Component[]): StateMap {
        // Replace the pipe flows with components.
        let stateMap: StateMap = this.buildComponentMap(components);
        stateMap = flows.filter(flow => flow.type !== FlowType.PIPE).reduce((map: StateMap, flow: Flow) => {
            map[flow.id] = flow;
            return map;
        }, stateMap);
        return stateMap;
    }

    private buildData(): any {
        return this.props.flows;
    }

    private stopTimer(): void {
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
    }

    public shouldComponentUpdate(nextProps: FlowsProps): boolean {
        if (nextProps.components !== this.props.components) {
            this.componentMap = this.buildComponentMap(nextProps.components);
            this.stateMap = this.buildStateMap(nextProps.flows, nextProps.components);
        }
        if (nextProps.flows !== this.props.flows) {
            this.stateMap = this.buildStateMap(nextProps.flows, nextProps.components);
        }
        return true;
    }

    public componentWillUnmount(): void {
        this.stopTimer();
    }

    public render(): ReactElement {
        const actions = !this.state.testingView ? [{
            icon: <EditOutlined />,
            label: "Edit flow",
            callback: this.handleFlowEdit
        }, {
            icon: <DeleteOutlined />,
            label: "Delete flow",
            confirm: "Are you sure you want to delete the flow?",
            callback: this.handleFlowDelete
        }, {
            icon: <UpOutlined />,
            label: "Move up",
            disabled: (flow: Flow, index: number) => {
                return index === 0;
            },
            callback: this.handleFlowMoveUp
        }, {
            icon: <DownOutlined />,
            label: "Move down",
            disabled: (flow: Flow, index: number) => {
                return index === this.props.flows.length - 1;
            },
            callback: this.handleFlowMoveDown
        }] : undefined;
        const columns = this.state.testingView ? this.buildTestColumns() : this.buildEditColumns();
        return (
            <div>
                {CoreUtils.isEmpty(this.state.index) &&
                    <div>
                        <ItemTable
                            className="x-flows"
                            size="small"
                            pagination={false}
                            columns={columns}
                            items={this.buildData()}
                            actions={actions}
                        />
                        <Spacer className="x-flows-add">
                            {!this.state.testingView &&
                                <Button
                                    disabled={this.state.testingView}
                                    onClick={this.handleFlowCreate}
                                >
                                    Add Flow
                                </Button>
                            }
                            {this.state.testingView && !this.state.isRunning &&
                                <Button
                                    onClick={this.handleEditShow}
                                >
                                    Exit Test
                                </Button>
                            }
                            {(!this.state.testingView || this.state.isRunning) &&
                                <Button
                                    onClick={this.handleWorkflowTest}
                                    loading={this.state.isRunning}
                                    disabled={this.props.components.length === 0}
                                >
                                    Test Workflow
                                </Button>
                            }
                            {this.state.testingMessage &&
                                <span>{this.state.testingMessage}</span>
                            }
                            {this.state.error &&
                                <span className="x-flows-error">{this.state.error.message}</span>
                            }
                        </Spacer>
                    </div>
                }
                {!CoreUtils.isEmpty(this.state.index) &&
                    <FlowEditor
                        converterMap={this.props.converterMap}
                        flow={this.state.flow}
                        index={this.state.index!}
                        flows={this.props.flows}
                        components={this.props.components}
                        componentMap={this.componentMap}
                        configurationSpecs={this.props.configurationSpecs}
                        authentications={this.props.authentications}
                        onDone={this.handleEditorDone}
                        onCancel={this.handleEditorCancel}
                    />
                }
                {this.state.executionId && this.state.executionState &&
                    <StateView
                        executionId={this.state.executionId}
                        state={this.state.executionState}
                        onClose={this.handleStateHide}
                    />
                }
            </div>

        );
    }

}
